import React, { useContext, useEffect, useCallback } from 'react';
import {
  Button, CardShowcase, Heading, ProgressCircle,
} from '@americanexpress/dls-react';
import { withRouter } from '@americanexpress/one-app-router';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import Wrapper from '../components/Wrapper';
import { BankContext } from '../utility/BankContext';
import { AmountSelectedContext } from '../utility/AmountSelectedContext';
import { paymentTypes } from '../assets/statementData';
import { DateContext } from '../utility/DateContext';
import useApiWithSession from '../utility/useApiWithSession';
import { PaymentContext } from '../utility/PaymentContext';
import { TokenContext } from '../utility/TokenContext';
import { formatDisplayDate } from '../utility/dateUtils';

// Helper: Format date from MM/DD/YYYY or YYYY-MM-DD to YYYY-MM-DD
const formatDate = (date) => {
  if (!date) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  const dateArray = date.split('/');
  if (dateArray.length === 3) {
    return `${dateArray[2]}-${dateArray[0].padStart(2, '0')}-${dateArray[1].padStart(2, '0')}`;
  }
  return date;
};

const PaymentConfirmation = ({ router }) => {
  // Contexts
  const { selectedBank } = useContext(BankContext);
  const { amountSelected, paymentType } = useContext(AmountSelectedContext);
  const { selectedDate } = useContext(DateContext);
  const {
    paymentResponse, setPaymentSchedule, setConfirmationCode, market1, journey1, sid1, u1,
  } = useContext(PaymentContext);
  const { gateWayUrl } = useContext(TokenContext);
  const dispatch = useDispatch();

  // API hooks
  const {
    isLoading, data, error, run,
  } = useApiWithSession({
    url: `${gateWayUrl}/voice/v1/multimodal/events`,
    sid1,
    method: 'PUT',
    body: {
      eventName: 'REVIEW_SUBMIT_PAYMENT',
      eventData: {
        accountToken: paymentResponse?.paymentOptions?.accountToken,
        amount: Number.parseFloat(amountSelected),
        option: paymentType,
        fundingAccountEnrollId: selectedBank?.arrangementManagerEnrollmentId,
        remittanceDate: formatDate(selectedDate),
        program: 'PAYBILL',
      },
    },
    customHeaders: {
      session_id: sid1,
      correlation_id: u1,
      consumer: 'GUI',
      market: market1,
      journey: journey1,
    },
  });

  const {
    isLoading: isLoadingBack, data: dataBack, error: errorBack, run: runBack,
  } = useApiWithSession({
    url: `${gateWayUrl}/voice/v1/multimodal/events`,
    sid1,
    method: 'PUT',
    body: { eventName: 'REVIEW_BACK' },
    customHeaders: {
      session_id: sid1,
      correlation_id: u1,
      consumer: 'GUI',
      market: market1,
      journey: journey1,
    },
  });

  // Redirect if paymentResponse is missing
  useEffect(() => {
    if (!paymentResponse) {
      router.push({ pathname: '/message', query: { level: 3 } });
    }
  }, [paymentResponse, router]);

  // Handle back API response
  useEffect(() => {
    if (isLoadingBack === false) {
      if (!errorBack && dataBack?.statusCode === 'success') {
        router.push({ pathname: '/payyourbill' });
      } else if (errorBack || dataBack?.statusCode === 'failure') {
        dispatch(addErrorToReport(errorBack || new Error('REVIEW_BACK failed'), { message: 'PaymentConfirmation: REVIEW_BACK event failed.' }));
        router.push({ pathname: '/message', query: { level: 1 } });
      }
    }
  }, [isLoadingBack, errorBack, dataBack, dispatch, router]);

  // Handle submit payment API response
  useEffect(() => {
    if (!isLoading && !error && data?.statusCode === 'success') {
      setConfirmationCode(data?.eventData?.submitPayment?.confirmationCode || '');
      setPaymentSchedule(data?.eventData?.submitPayment?.paymentFrequency === 'SCHEDULED');
      router.push({ pathname: '/finish' });
    } else if (!isLoading && (error || data?.statusCode === 'failure')) {
      dispatch(addErrorToReport(error || new Error('REVIEW_SUBMIT_PAYMENT failed'), { message: 'PaymentConfirmation: REVIEW_SUBMIT_PAYMENT event failed.' }));
      router.push({ pathname: '/message', query: { level: 3 } });
    }
  }, [isLoading, error, data, setConfirmationCode, setPaymentSchedule, dispatch, router]);

  // Back handler
  const handleBack = useCallback(async () => {
    const sessionId = document.cookie
      .split('; ')
      .find((row) => row.startsWith('session_id='))
      ?.split('=')[1];
    if (!sessionId) {
      router.push({ pathname: '/message', query: { level: 3 } });
      return;
    }
    await runBack();
  }, [runBack, router]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      handleBack();
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handleBack]);

  // Submit payment handler
  const handleSubmitPayment = useCallback(async () => {
    const sessionId = document.cookie
      .split('; ')
      .find((row) => row.startsWith('session_id='))
      ?.split('=')[1];
    if (!sessionId) {
      router.push({ pathname: '/message', query: { level: 3 } });
      return;
    }
    await run();
  }, [run, router]);

  return (
    <Wrapper>
      <div className="width-full">
        <Heading
          level={4}
          headingStyle="heading4"
          style={{ fontWeight: 700 }}
          className="pad-3-t pad-2-l"
        >
          {paymentResponse?.productInfo?.product_desc}
          {paymentResponse?.productInfo?.display_account_number ? `-${paymentResponse.productInfo.display_account_number.slice(-5)}` : ''}
        </Heading>
        {isLoading ? (
          <div className="margin-4-t margin-2-b flex flex-column flex-justify-center flex-align-center text-align-center">
            <ProgressCircle />
            <p className="legal-1">Trying to submit your payment. Don&apos;t leave the screen!</p>
          </div>
        ) : (
          <CardShowcase className="anim-fade-in anim-delay-1 width-full pad-1 card-rounded">
            <div className="pad-1-lr pad-2-tb card-rounded flex flex-column flex-align-start" style={{ gap: '1rem' }}>
              <div className="flex flex-column width-full text-align-left">
                <Heading
                  headingStyle="heading3"
                  level={3}
                >
                  Bank Account
                </Heading>
                <p className="body-3">
                  {selectedBank?.financialInstitutionAccount?.institutionName} -
                  {selectedBank?.financialInstitutionAccount?.accountIdentifierRedacted}
                </p>
              </div>
              <div className="flex flex-column width-full text-align-left">
                <Heading
                  headingStyle="heading3"
                  level={3}
                >
                  Amount
                </Heading>
                <p className="body-3">
                  ${Number(amountSelected).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}<br />
                  {paymentTypes[paymentType]}
                </p>
              </div>
              <div className="flex flex-column width-full text-align-left">
                <Heading
                  headingStyle="heading3"
                  level={3}
                >
                  Date
                </Heading>
                <p className="body-3">{formatDisplayDate(selectedDate)}</p>
              </div>
              <hr />
              <p className="legal-1 text-align-left">
                If you&apos;ve selected today as your payment date,
                you may not be able to change it or cancel this payment once submitted.
                By selecting &quot;Submit Payment&quot; you authorize American Express to
                debit your account as described on this screen
              </p>
              <Button
                type="button"
                className="width-full"
                styleType="primary"
                style={{ maxWidth: '100%' }}
                onClick={handleSubmitPayment}
              >
                <p className="heading-3">Submit Payment</p>
              </Button>
              <Button
                className="width-full"
                styleType="secondary"
                style={{ maxWidth: '100%' }}
                onClick={handleBack}
              >
                <p className="heading-3">Back</p>
              </Button>
            </div>
          </CardShowcase>
        )}
      </div>
    </Wrapper>
  );
};

PaymentConfirmation.propTypes = {
  router: PropTypes.instanceOf(Object).isRequired,
};

export default withRouter(PaymentConfirmation);
